import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError, qs } from '../api/client';
import { useAuth } from '../state/auth';
import { useAlertes } from '../state/alertes';
import { Badge, Bandeau, Carte, Champ, Kpi, Modale, Squelette, Vide } from '../components/ui';
import { NotesDecalage } from '../components/DecalageEncaissement';
import { centimes, dateFr, heureFr, isoJour, libelleMouvement, moisLong, montant, nomClient } from '../lib/format';
import { T } from '../i18n';
import type { BilanDetaille, BilanMois, Contrat, EntreesDuMois, Mouvement, Rendement, Tiroir, Tresorerie } from '../api/types';

/** Le mois precedent / suivant, en 'YYYY-MM'. */
function moisDecale(mois: string, n: number): string {
  const [a, m] = mois.split('-').map(Number);
  const d = new Date(a!, (m ?? 1) - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
/**
 * Les mois proposés dans le sélecteur.
 *
 * Dix-huit mois en arrière, six en avant : assez loin pour retrouver l'exercice
 * passé, assez près pour que la liste reste lisible. Le mois affiché y est
 * toujours ajouté, même hors de cette fenêtre — sinon arriver sur un mois par
 * un lien le ferait disparaître de son propre sélecteur.
 */
const MOIS_CHOISISSABLES = (courant: string): string[] => {
  const aujourdhui = isoJour(new Date()).slice(0, 7);
  const liste = new Set<string>();
  for (let n = -18; n <= 6; n++) liste.add(moisDecale(aujourdhui, n));
  liste.add(courant);
  return [...liste].sort().reverse();
};

const libelleMois = (mois: string) => {
  const [a, m] = mois.split('-').map(Number);
  return moisLong(new Date(a!, (m ?? 1) - 1, 1));
};

const LIBELLE_JOUR = (): Record<string, string> => T.caisse.etatsJour;
/**
 * Le jour de la semaine du n-ieme jour du mois affiche.
 * Un calendrier qui n'indique pas les samedis et dimanches ne se lit pas :
 * le patron veut savoir si la voiture dormait un week-end ou un mardi.
 */
function numJour(mois: string, index: number): number {
  const [a, m] = mois.split('-').map(Number);
  return new Date(a!, m! - 1, index + 1).getDay();
}
function jourSemaine(mois: string, index: number): string {
  return T.caisse.jours[numJour(mois, index)]!;
}
/** Samedi ou dimanche, quelle que soit la langue. */
const estWeekend = (mois: string, index: number) => [0, 6].includes(numJour(mois, index));

/** Le jour du mois affiché est-il aujourd'hui ? Sert au liseré sur le calendrier. */
function estAujourdhui(mois: string, index: number): boolean {
  const n = new Date();
  return mois === isoJour(n).slice(0, 7) && n.getDate() === index + 1;
}

interface Tiroirs { tiroirs: Tiroir[]; totalReferenceMinor: number; tresorerie: Tresorerie; deviseReference: string }
interface Devises { devises: { code: string; libelle: string; decimales: number }[]; taux: { devise: string; tauxAppliqueMinor: number; dateEffet: string | null; manquant?: boolean }[]; deviseReference: string }

/** Un tiroir par devise. Pas de melange, pas de conversion implicite. */
export default function Caisse() {
  const { peut, estAgent } = useAuth();
  // La pastille « Dettes » doit tomber au moment ou l'argent rentre.
  const { recharger: rechargerAlertes } = useAlertes();
  const [t, setT] = useState<Tiroirs | null>(null);
  const [dev, setDev] = useState<Devises | null>(null);
  const [jour, setJour] = useState(isoJour(new Date()));
  /*
   * « Hichem aime beaucoup voir les finances par mois, du 1er au 31. »
   * La caisse ne se lisait qu'au jour le jour : impossible de repondre a
   * « combien Sabrina a-t-elle depense ce mois-ci ».
   */
  /*
   * Le mois d'abord.
   *
   * Le patron ouvre la caisse pour voir le mois et le rendement de ses
   * voitures, pas le detail de la journee. Le jour reste a un clic — c'est la
   * vue de Sabrina au comptoir, mais ce n'est pas la premiere question posee.
   */
  /*
   * La vue vit aussi dans l'adresse : /caisse?vue=jour.
   * Le mois y était déjà ; sans la vue, impossible d'envoyer à quelqu'un un
   * lien vers la caisse du jour — ni de la montrer dans le guide.
   */
  const moisCourant = isoJour(new Date()).slice(0, 7);
  /*
   * Le mois regardé vit dans l'adresse (?mois=2026-10).
   *
   * Les rappels « déjà encaissés en septembre » font aller et venir entre deux
   * mois et la fiche d'une voiture. Gardé dans un simple état, le mois repartait
   * sur le mois en cours à chaque retour : il fallait le rechoisir à chaque fois.
   */
  const [params, setParams] = useSearchParams();
  const vueDemandee = params.get('vue');
  const vue: 'jour' | 'mois' = vueDemandee === 'jour' ? 'jour' : 'mois';
  const setVue = (v: 'jour' | 'mois') => {
    if (v === 'mois') params.delete('vue'); else params.set('vue', v);
    setParams(params, { replace: true });
  };
  const moisDemande = params.get('mois');
  const mois = moisDemande && /^\d{4}-\d{2}$/.test(moisDemande) ? moisDemande : moisCourant;
  const setMois = (m: string) => {
    if (m === moisCourant) params.delete('mois'); else params.set('mois', m);
    setParams(params, { replace: true });
  };
  const [bilan, setBilan] = useState<BilanMois | null>(null);
  const [rendement, setRendement] = useState<Rendement | null>(null);
  const [entrees, setEntrees] = useState<EntreesDuMois | null>(null);
  /* Ou est passe l'argent : ce qui est sorti, ce qu'on nous doit, et ce qui
   * est deja encaisse pour des locations qui n'ont pas eu lieu. */
  const [bilanD, setBilanD] = useState<BilanDetaille | null>(null);
  const [mvts, setMvts] = useState<Mouvement[]>([]);
  const [ecran, setEcran] = useState<'' | 'encaisser' | 'change' | 'cloture' | 'taux'>('');
  const [msg, setMsg] = useState('');
  // Un encaissement anonyme ne solde aucun dossier. Quand on arrive depuis un
  // contrat (?contrat=<id>), la modale s'ouvre pre-remplie et le mouvement est
  // rattache : c'est ce rattachement qui fait vivre le solde et la tresorerie.
  const [cible, setCible] = useState<Contrat | null>(null);
  const idContrat = params.get('contrat');

  useEffect(() => {
    if (!idContrat) { setCible(null); return; }
    api.get<{ contrat: Contrat }>(`/contrats/${idContrat}`)
      .then((d) => { setCible(d.contrat); setEcran('encaisser'); })
      .catch(() => setMsg(T.caisse.dossierIntrouvable));
  }, [idContrat]);

  const fermerEncaissement = () => {
    setEcran('');
    if (idContrat) { params.delete('contrat'); setParams(params, { replace: true }); }
  };

  const recharger = useCallback(() => {
    api.get<Tiroirs>('/caisse/tiroirs').then(setT);
    api.get<Mouvement[]>(`/caisse/mouvements${qs({ date: jour })}`).then(setMvts);
    api.get<Devises>('/caisse/devises').then(setDev);
  }, [jour]);
  useEffect(recharger, [recharger]);

  useEffect(() => {
    if (vue !== 'mois') return;
    setBilan(null);
    api.get<BilanMois>(`/caisse/bilan-mensuel${qs({ mois })}`).then(setBilan).catch(() => {});
    setRendement(null); setEntrees(null);
    api.get<Rendement>(`/caisse/rendement${qs({ mois })}`).then(setRendement).catch(() => {});
    api.get<EntreesDuMois>(`/caisse/entrees-du-mois${qs({ mois })}`).then(setEntrees).catch(() => {});
    api.get<BilanDetaille>(`/caisse/bilan-detaille${qs({ mois })}`).then(setBilanD).catch(() => {});
  }, [vue, mois]);

  /*
   * UN MOIS À VENIR N'A PAS ENCORE D'ARGENT.
   *
   * Hichem a ouvert novembre et y a vu 242 500 DZD : c'était le solde du tiroir
   * d'aujourd'hui, reporté sous un titre « novembre ». Même piège pour le
   * « généré » (ce que les locations réservées rapporteront) et pour les dettes
   * (ce qu'on nous doit à ce jour). Sur un mois à venir, ces chiffres existent
   * toujours — mais ils se nomment autrement, sinon on lit de l'argent reçu là
   * où il n'y en a aucun.
   */
  const aVenir = mois > moisCourant;

  if (!t || !dev) return <Squelette lignes={6} />;
  const tx = T.caisse;
  const etatsJour = LIBELLE_JOUR();

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{tx.titre}</h1>
          <p>{tx.sousTitre(t.deviseReference)}</p>
        </div>
        <div className="actions">
          <button className={`btn btn-s ${vue === 'jour' ? 'btn-p' : ''}`} onClick={() => setVue('jour')}>{tx.vueJour}</button>
          <button className={`btn btn-s ${vue === 'mois' ? 'btn-p' : ''}`} onClick={() => setVue('mois')}>{tx.vueMois}</button>
          <button className="btn btn-p" onClick={() => setEcran('encaisser')}>{tx.encaisser}</button>
          {peut('modifier.finance') && <button className="btn" onClick={() => setEcran('change')}>{tx.operationChange}</button>}
          {peut('gerer.taux') && <button className="btn" onClick={() => setEcran('taux')}>{tx.tauxDuJour}</button>}
          <button className="btn" disabled={!peut('cloturer.caisse')} onClick={() => setEcran('cloture')}
                  title={peut('cloturer.caisse') ? undefined : tx.reserveGerants}>
            {tx.cloturer}
          </button>
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}

      {vue === 'mois' && (
        <div style={{ marginBottom: 18 }}>
          {/*
            * Un SÉLECTEUR, et les mois à venir ouverts.
            *
            * Il fallait cliquer « ‹ » huit fois pour revoir janvier, et la flèche
            * « › » était bloquée au mois courant : l'avenir était inatteignable.
            * Or il dit quelque chose — une location d'octobre déjà réservée y
            * apparaît, avant d'être encaissée. C'est exactement ce qu'un patron
            * veut voir quand il prépare sa trésorerie.
            */}
          <div className="actions" style={{ marginBottom: 12, alignItems: 'center' }}>
            <button className="btn btn-s" onClick={() => setMois(moisDecale(mois, -1))}
                    title={tx.selecteur.precedent}>‹</button>
            <select value={mois} onChange={(e) => setMois(e.target.value)}
                    style={{ minWidth: 190, textAlign: 'center', fontWeight: 700 }}
                    aria-label={tx.selecteur.choisir}>
              {MOIS_CHOISISSABLES(mois).map((m) => (
                <option key={m} value={m}>
                  {libelleMois(m)}{m === moisCourant ? tx.selecteur.enCours : m > moisCourant ? tx.selecteur.aVenir : ''}
                </option>
              ))}
            </select>
            <button className="btn btn-s" onClick={() => setMois(moisDecale(mois, 1))}
                    title={tx.selecteur.suivant}>›</button>
            {mois !== moisCourant && (
              <button className="btn btn-s" onClick={() => setMois(moisCourant)}>{tx.selecteur.revenir}</button>
            )}
          </div>

          {/*
            * Un mois à venir : sa caisse est vide, ses locations ne le sont pas.
            *
            * L'argent se compte au mois où il est REÇU. Une location d'octobre
            * payée en septembre est dans la caisse de septembre : octobre montre
            * la location, pas son argent — et chaque voiture le rappelle.
            */}
          {mois > moisCourant && (
            <div style={{ marginBottom: 12 }}>
              <Bandeau type="info" titre={tx.moisAVenir.titre}>
                <b>{tx.moisAVenir.gras1(libelleMois(mois))}</b>{tx.moisAVenir.texte1}<b>{tx.moisAVenir.gras2}</b>
                {tx.moisAVenir.texte2}<b>{tx.moisAVenir.gras3}</b>{tx.moisAVenir.texte3}<b>{tx.moisAVenir.gras4}</b>
                {tx.moisAVenir.texte4(libelleMois(mois))}
              </Bandeau>
            </div>
          )}

          {bilan === null ? <Squelette lignes={4} /> : (
            <>
              <div className="grille g4" style={{ marginBottom: 14 }}>
                <div className="carte carte-p">
                  <div className="micro">{tx.bilan.fonds}</div>
                  <div className="v montant">{montant(bilan.fondsMinor)}</div>
                  <div className="mini sourdine">{tx.bilan.fondsDetail}</div>
                </div>
                <div className="carte carte-p">
                  <div className="micro">{tx.bilan.encaisse}</div>
                  <div className="v montant" style={{ color: 'var(--succes)' }}>{montant(bilan.encaissementsMinor)}</div>
                </div>
                <div className="carte carte-p">
                  <div className="micro">{tx.bilan.depense}</div>
                  <div className="v montant" style={{ color: 'var(--danger)' }}>{montant(bilan.depensesMinor)}</div>
                  {bilan.fondsMinor > 0 && (
                    <div className="mini sourdine">
                      {tx.bilan.fondsConsomme(Math.round((bilan.depensesMinor / bilan.fondsMinor) * 100))}
                    </div>
                  )}
                </div>
                <div className="carte carte-p signature-kpi">
                  <div className="micro">{aVenir ? tx.bilan.soldeAujourdhui : tx.bilan.solde}</div>
                  <div className="v">{montant(bilan.soldeFinMinor)}</div>
                  <div className="decomposition">
                    {aVenir
                      ? <span>{tx.bilan.maintenantAvant}<b>{tx.bilan.maintenantGras}</b>{tx.bilan.maintenantApres(libelleMois(mois))}</span>
                      : <span>{tx.bilan.auPremier}<b>{montant(bilan.soldeDebutMinor)}</b></span>}
                    {!aVenir && bilan.transfertsSortantsMinor > 0 && <span>{tx.bilan.verseBanque}<b>{montant(bilan.transfertsSortantsMinor)}</b></span>}
                  </div>
                </div>
              </div>

              {/* La vraie question du patron : qui a depensé quoi. */}
              <Carte pad={false} titre={tx.quiAFaitQuoi}>
                <div className="enveloppe-tbl">
                  <table className="tbl">
                    <thead><tr><th>{tx.colonnesPersonnes.personne}</th><th className="droite">{tx.colonnesPersonnes.operations}</th><th className="droite">{tx.colonnesPersonnes.encaisse}</th><th className="droite">{tx.colonnesPersonnes.depense}</th></tr></thead>
                    <tbody>
                      {bilan.parPersonne.map((p) => (
                        <tr key={p.nom}>
                          <td><b>{p.nom}</b></td>
                          <td className="droite num">{p.nbOperations}</td>
                          <td className="droite montant">{p.encaissementsMinor > 0 ? montant(p.encaissementsMinor) : '—'}</td>
                          <td className="droite montant" style={p.depensesMinor > 0 ? { color: 'var(--danger)' } : undefined}>
                            {p.depensesMinor > 0 ? montant(p.depensesMinor) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Carte>

              <p className="mini sourdine" style={{ marginTop: 12 }}>
                {tx.noteNominative}
              </p>

              {/*
                * Le croquis du patron : une fiche par voiture, un petit calendrier
                * du mois, et ce qu'elle rapporte. Sa question n'est pas « quel est
                * le chiffre d'affaires » mais « laquelle de mes treize voitures est
                * restée sur le parking ce mois-ci ».
                */}
              {rendement && (
                <div style={{ marginTop: 20 }}>
                  <Carte titre={tx.rendement.titre(libelleMois(mois))} action={
                    <div className="legende" style={{ margin: 0 }}>
                      <span><i style={{ background: 'var(--succes)' }} />{tx.rendement.legende.louee}</span>
                      <span><i style={{ background: 'var(--violet)' }} />{tx.rendement.legende.reservee}</span>
                      <span><i style={{ background: '#8A929E' }} />{tx.rendement.legende.entretien}</span>
                      <span><i style={{ background: 'var(--danger-doux)', border: '1px solid var(--danger-bord)' }} />{tx.rendement.legende.libre}</span>
                    </div>
                  }>
                    <div className="grille g4" style={{ marginBottom: 16 }}>
                      <Kpi libelle={aVenir ? tx.rendement.prevu : tx.rendement.genere} valeur={montant(rendement.totalGenereMinor)}
                           detail={aVenir
                             ? tx.rendement.prevuDetail
                             : tx.rendement.genereDetail(rendement.vehicules.filter((v) => v.joursLoues > 0).length, rendement.vehicules.length)} />
                      {/*
                        * « pour ces voitures », pas « Encaissé » tout court : la carte du haut
                        * compte la caisse seule, celle-ci l'argent des locations, caisse ET
                        * banque. Même nom, deux chiffres différents, et le gérant croit à une erreur.
                        */}
                      <Kpi libelle={tx.rendement.encaissePourVoitures} valeur={montant(rendement.totalEncaisseMinor)}
                           detail={[
                             rendement.totalAvanceMinor > 0 ? tx.rendement.dontAvance(montant(rendement.totalAvanceMinor)) : '',
                             rendement.totalBanqueMinor > 0 ? tx.rendement.dontBanque(montant(rendement.totalBanqueMinor)) : '',
                             rendement.totalAvanceMinor === 0 && rendement.totalDejaEncaisseMinor > 0
                               ? tx.rendement.dejaAvant(montant(rendement.totalDejaEncaisseMinor)) : '',
                           ].filter(Boolean).join(' · ') || tx.rendement.encaisseDetail} />
                      <Kpi libelle={tx.rendement.reservePlusTard} valeur={montant(rendement.totalReserveApresMinor)}
                           detail={tx.rendement.reservePlusTardDetail} />
                      <Kpi libelle={tx.rendement.immobiles} valeur={String(rendement.vehicules.filter((v) => v.joursLoues === 0 && v.joursReserves === 0).length)}
                           detail={tx.rendement.immobilesDetail} />
                    </div>

                    <div className="rendement">
                      {/* On clique la voiture et on voit d'ou vient son chiffre :
                        * ses locations, ses mouvements de caisse, ses depenses. */}
                      {rendement.vehicules.map((v) => (
                        <Link key={v.vehicleId} className="carte rv-lien"
                              to={`/caisse/voiture/${v.vehicleId}${qs({ mois })}`}>
                          <div className="rv-tete">
                            <span className="rv-nom">{v.libelle}</span>
                          </div>
                          <div className="mini sourdine">
                            {v.immatriculation} · {tx.rendement.parJour(montant(v.tarifJourMinor))}
                          </div>

                          {/*
                            * Le mois se lit de haut en bas, un jour par ligne, avec sa date
                            * et son jour de semaine. Les journees identiques forment un bloc
                            * de couleur continu : on voit d'un coup ou la voiture a dormi.
                            */}
                          <ol className="cal-v">
                            {v.jours.map((e, i) => (
                              <li key={i}
                                  className={`${e !== 'libre' ? e : ''}${estAujourdhui(mois, i) ? ' auj' : ''}`
                                    + (estWeekend(mois, i) ? ' we' : '')}>
                                <span className="cal-n">{i + 1}</span>
                                <span className="cal-s">{jourSemaine(mois, i)}</span>
                                <span className="cal-b" title={`${i + 1} — ${etatsJour[e]}`} />
                              </li>
                            ))}
                          </ol>

                          <div className="rv-pied">
                            <span>
                              {tx.rendement.joursLoues(v.joursLoues)}
                              {v.joursReserves > 0 && tx.rendement.joursReserves(v.joursReserves)}
                              {v.joursEntretien > 0 && tx.rendement.joursGarage(v.joursEntretien)}
                            </span>
                            <span>{tx.rendement.locations(v.nbLocations)}</span>
                          </div>

                          {v.reserveApresJours > 0 && (
                            <div className="mini" style={{ marginTop: 8, color: 'var(--violet)' }}>
                              {tx.rendement.reserveApres(v.reserveApresJours)}
                              <b>{montant(v.reserveApresMinor)}</b>
                            </div>
                          )}
                          {v.joursLoues === 0 && v.joursReserves === 0 && (
                            <div className="mini" style={{ marginTop: 8, color: 'var(--danger)' }}>
                              {tx.rendement.pasSortie}
                            </div>
                          )}

                          {/* Le chiffre de la voiture ferme la carte : on lit le mois,
                            * puis ce qu'il a rapporte. C'est la conclusion, pas l'entree. */}
                          <div className="rv-total">
                            <span>{aVenir ? tx.rendement.prevu : tx.rendement.genere}</span>
                            <b className="montant" style={{ color: v.genereMinor === 0 ? 'var(--danger)' : undefined }}>
                              {montant(v.genereMinor)}
                            </b>
                            {/*
                              * L'argent REÇU ce mois-ci pour cette voiture, quelle que soit
                              * la date de ses locations — et, juste dessous, où est passé
                              * celui qui n'appartient pas au mois de la location.
                              */}
                            <div className="rv-encaisse">
                              <span>{tx.rendement.encaisseMois}</span>
                              <b className="montant">{montant(v.encaisseMinor)}</b>
                            </div>
                            <NotesDecalage e={v} mois={mois} />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </Carte>
                </div>
              )}

              {/* D'ou vient l'argent qui n'est pas de la location. */}
              {entrees && (
                <div className="grille g2" style={{ marginTop: 20, alignItems: 'start' }}>
                  <Carte titre={tx.supplements.titre(montant(entrees.supplements.totalMinor))} pad={false}>
                    {entrees.supplements.total === 0 ? (
                      <div className="carte-p"><Vide titre={tx.supplements.vide}>
                        {tx.supplements.videTexte}
                      </Vide></div>
                    ) : (
                      <div className="enveloppe-tbl">
                        <table className="tbl compact">
                          <thead><tr><th>{tx.supplements.colonnes.supplement}</th><th>{tx.supplements.colonnes.calcul}</th><th>{tx.supplements.colonnes.voiture}</th><th className="droite">{tx.supplements.colonnes.montant}</th></tr></thead>
                          <tbody>
                            {entrees.supplements.lignes.map((l, i) => (
                              <tr key={i}>
                                <td><b>{l.quoi}</b><div className="mini sourdine">{l.client}</div></td>
                                <td className="mini sourdine">{l.detail}</td>
                                {/* La voiture d'abord : c'est elle qu'on reconnaît.
                                    Le numéro de contrat reste dessous, en petit. */}
                                <td className="mini">
                                  <b>{l.vehicule ?? l.contrat}</b>
                                  <div className="sourdine">{l.immatriculation ?? l.contrat}</div>
                                </td>
                                <td className="droite montant">{montant(l.montantMinor)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Carte>

                  <Carte titre={tx.cautions.titre(montant(entrees.cautions.totalMinor))} pad={false}>
                    {entrees.cautions.total === 0 ? (
                      <div className="carte-p"><Vide titre={tx.cautions.vide} /></div>
                    ) : (
                      <div className="enveloppe-tbl">
                        <table className="tbl compact">
                          <thead><tr><th>{tx.cautions.colonnes.mouvement}</th><th>{tx.cautions.colonnes.detail}</th><th>{tx.cautions.colonnes.par}</th><th className="droite">{tx.cautions.colonnes.montant}</th></tr></thead>
                          <tbody>
                            {entrees.cautions.lignes.map((l, i) => (
                              <tr key={i}>
                                <td><b>{l.quoi}</b><div className="mini sourdine">{dateFr(l.date)}</div></td>
                                <td className="mini sourdine">{l.detail}</td>
                                <td className="mini sourdine">{l.par ?? '—'}</td>
                                <td className="droite montant"
                                    style={{ color: l.montantMinor < 0 ? 'var(--danger)' : undefined }}>
                                  {montant(l.montantMinor)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="mini sourdine" style={{ padding: '10px 14px 12px' }}>
                      {tx.cautions.note}
                    </p>
                  </Carte>
                </div>
              )}

              {/*
                * Ou est passe l'argent.
                *
                * Le solde d'un mois ne dit pas tout : il ne dit pas ce qui a
                * quitte la caisse, ce qu'on nous doit encore, ni quelle part
                * du solde est deja promise a des locations pas encore livrees.
                * Chaque mois se relit tel quel : c'est l'archive.
                */}
              {bilanD && (
                <div style={{ marginTop: 20 }}>
                  <Carte titre={tx.ouArgent.titre(libelleMois(mois))}>
                    <div className="grille g3" style={{ marginBottom: 16 }}>
                      <Kpi libelle={tx.ouArgent.sorti} valeur={montant(bilanD.sortiesMinor)}
                           detail={tx.ouArgent.sortiDetail} />
                      {/* Les dettes ne sont pas « du mois » : c'est ce qui reste dû à ce jour,
                        * tous mois confondus. Sous un titre « novembre », on le dit. */}
                      <Kpi libelle={aVenir ? tx.ouArgent.doitAujourdhui : tx.ouArgent.doit}
                           valeur={montant(bilanD.dettesMinor)}
                           detail={tx.ouArgent.doitDetail(bilanD.dettes.length, aVenir)} />
                      <Kpi libelle={tx.ouArgent.avances} valeur={montant(bilanD.avancesMinor)}
                           detail={tx.ouArgent.avancesDetail} />
                    </div>

                    <div className="grille g2" style={{ alignItems: 'start' }}>
                      <div>
                        <div className="micro" style={{ marginBottom: 8 }}>{tx.ouArgent.sorties}</div>
                        {bilanD.sorties.length === 0 ? (
                          <p className="mini sourdine">{tx.ouArgent.rienSorti}</p>
                        ) : (
                          <div className="enveloppe-tbl">
                            <table className="tbl compact">
                              <thead><tr><th>{tx.ouArgent.colonnesSorties.date}</th><th>{tx.ouArgent.colonnesSorties.motif}</th><th>{tx.ouArgent.colonnesSorties.vers}</th><th>{tx.ouArgent.colonnesSorties.par}</th><th className="droite">{tx.ouArgent.colonnesSorties.montant}</th></tr></thead>
                              <tbody>
                                {bilanD.sorties.map((x, i) => (
                                  <tr key={i}>
                                    <td className="mini num">{dateFr(x.date)}</td>
                                    <td className="mini">{x.libelle}</td>
                                    <td className="mini sourdine">{x.vers}</td>
                                    <td className="mini">{x.par}</td>
                                    <td className="droite montant" style={{ color: 'var(--danger)' }}>−{montant(x.montantMinor)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="micro" style={{ marginBottom: 8 }}>{tx.ouArgent.doit}</div>
                        {bilanD.dettes.length === 0 ? (
                          <p className="mini sourdine">{tx.ouArgent.personne}</p>
                        ) : (
                          <div className="enveloppe-tbl">
                            <table className="tbl compact">
                              <thead><tr><th>{tx.ouArgent.colonnesDettes.client}</th><th>{tx.ouArgent.colonnesDettes.voiture}</th><th>{tx.ouArgent.colonnesDettes.dossier}</th><th className="droite">{tx.ouArgent.colonnesDettes.resteDu}</th></tr></thead>
                              <tbody>
                                {bilanD.dettes.map((l) => (
                                  <tr key={l.contratId}>
                                    <td className="mini"><b>{l.client}</b></td>
                                    <td className="mini sourdine">{l.vehicule}</td>
                                    <td className="mini sourdine">
                                      {l.numero}
                                      {l.reglement === 'differe' && tx.ouArgent.aCredit}
                                      {l.reglement === 'par_tranche' && tx.ouArgent.parTranches}
                                    </td>
                                    <td className="droite montant" style={{ color: 'var(--danger)' }}>{montant(l.duMinor)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ marginTop: 18 }}>
                      <div className="micro" style={{ marginBottom: 8 }}>
                        {tx.ouArgent.avancesTitre}
                      </div>
                      {bilanD.avances.length === 0 ? (
                        <p className="mini sourdine">{tx.ouArgent.aucuneAvance}</p>
                      ) : (
                        <div className="enveloppe-tbl">
                          <table className="tbl compact">
                            <thead>
                              <tr>
                                <th>{tx.ouArgent.colonnesAvances.dossier}</th><th>{tx.ouArgent.colonnesAvances.voiture}</th><th>{tx.ouArgent.colonnesAvances.client}</th><th>{tx.ouArgent.colonnesAvances.periode}</th>
                                <th className="droite">{tx.ouArgent.colonnesAvances.encaisse}</th><th className="droite">{tx.ouArgent.colonnesAvances.nonLivre}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bilanD.avances.map((a) => (
                                <tr key={a.numero}>
                                  <td className="mini">{a.numero}</td>
                                  <td className="mini">
                                    {a.vehicule}
                                    {a.immatriculation && <div className="sourdine num">{a.immatriculation}</div>}
                                  </td>
                                  <td className="mini">{a.client}</td>
                                  <td className="mini sourdine" style={{ whiteSpace: 'nowrap' }}>
                                    {dateFr(a.debut)} → {dateFr(a.fin)}
                                  </td>
                                  <td className="droite montant sourdine">{montant(a.encaisseMinor)}</td>
                                  <td className="droite montant" style={{ color: 'var(--violet)', fontWeight: 700 }}>
                                    {montant(a.nonLivreMinor)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <p className="mini sourdine" style={{ marginTop: 10, maxWidth: 620 }}>
                        {tx.ouArgent.avancesNote}
                      </p>
                    </div>
                  </Carte>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {vue === 'jour' && (
      <>
      <div className="tiroirs">
        {t.tiroirs.map((x) => (
          <div key={x.devise} className={`tiroir ${x.devise === t.deviseReference ? 'ref' : ''}`}>
            <div className="code">{x.devise} · {x.libelle}</div>
            <div className="solde">{montant(x.soldeMinor, '', x.decimales)}</div>
            {x.devise !== t.deviseReference && <div className="cv">≈ {montant(x.contrevaleurMinor, t.deviseReference)}</div>}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14 }} className="grille g2">
        <Carte>
          <div className="micro">{tx.jour.totalConsolide}</div>
          <div className="kpi"><div className="v">{montant(t.totalReferenceMinor, t.deviseReference)}</div></div>
          <div style={{ marginTop: 10 }}>
            <Bandeau type="accent">
              {tx.jour.dont}<b>{montant(t.tresorerie.prestationsFuturesEncaisseesMinor)}</b>{tx.jour.futures}
              <b>{montant(t.tresorerie.cautionsDetenuesMinor)}</b>{tx.jour.cautions}<b>{tx.jour.nonDisponibles}</b>{tx.jour.fin}
            </Bandeau>
          </div>
        </Carte>
        <Carte className="signature-kpi">
          <div className="micro">{tx.jour.disponible}</div>
          <div className="kpi"><div className="v" style={{ fontSize: 30 }}>{montant(t.tresorerie.disponibleMinor)}</div></div>
          <div className="decomposition">
            {/* Ce total additionne les QUATRE poches (caisse, banque, dépenses, extra).
              * L'appeler « caisse » faisait croire au seul tiroir, et le chiffre
              * paraissait faux à côté du total consolidé juste à gauche. */}
            <span>{tx.jour.toutesPoches}<b>{montant(t.tresorerie.soldeCaisseMinor)}</b></span>
            <span>{tx.jour.moinsFutures}<b>{montant(t.tresorerie.prestationsFuturesEncaisseesMinor)}</b></span>
            <span>{tx.jour.moinsCautions}<b>{montant(t.tresorerie.cautionsDetenuesMinor)}</b></span>
          </div>
        </Carte>
      </div>

      <div style={{ marginTop: 16 }}>
        <Carte titre={tx.jour.mouvements} pad={false}
               action={<input type="date" value={jour} onChange={(e) => setJour(e.target.value)} style={{ height: 32, borderRadius: 8, border: '1px solid var(--bord-fort)', padding: '0 8px' }} />}>
          {mvts.length === 0 ? <div className="carte-p"><Vide titre={tx.jour.aucunMouvement} /></div> : (
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead>
                  <tr><th>{tx.jour.colonnes.heure}</th><th>{tx.jour.colonnes.type}</th><th>{tx.jour.colonnes.libelle}</th><th>{tx.jour.colonnes.mode}</th><th>{tx.jour.colonnes.devise}</th>
                      <th className="droite">{tx.jour.colonnes.montant}</th><th className="droite">{tx.jour.colonnes.taux}</th>
                      <th className="droite">{tx.jour.colonnes.contrevaleur}</th><th>{tx.jour.colonnes.agent}</th></tr>
                </thead>
                <tbody>
                  {mvts.map((m) => (
                    <tr key={m._id}>
                      <td className="num">{heureFr(m.date)}</td>
                      <td><Badge statut={m.type.startsWith('caution') ? 'retour' : m.type.startsWith('change') ? 'entretien' : ['depense', 'retrait'].includes(m.type) ? 'retard' : 'en_cours'}
                                 libelle={libelleMouvement(m.type)} /></td>
                      <td>{m.libelle}
                        {m.gainChangeMinor !== undefined && m.gainChangeMinor !== 0 && (
                          <div className="mini" style={{ color: m.gainChangeMinor > 0 ? 'var(--succes)' : 'var(--danger)' }}>
                            {tx.jour.resultatChange(m.gainChangeMinor > 0, montant(Math.abs(m.gainChangeMinor)))}
                          </div>)}
                      </td>
                      <td className="mini sourdine">
                        {LIBELLE_MODE()[m.mode] ?? m.mode}
                        {m.compte === 'banque' && <div style={{ color: 'var(--primaire)' }}>{tx.jour.versBanque}</div>}
                      </td>
                      <td><b>{m.devise}</b></td>
                      <td className="droite montant">{montant(m.montantMinor, '')}</td>
                      <td className="droite num mini sourdine">{m.devise === t.deviseReference ? '—' : (m.tauxAppliqueMinor / 100).toFixed(2)}</td>
                      <td className="droite montant">{montant(m.contrevaleurMinor, '')}</td>
                      <td className="mini sourdine">{m.creePar?.nom ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      </div>
      </>
      )}

      {estAgent && (
        <p className="mini sourdine" style={{ marginTop: 12 }}>
          {tx.noteAgent}
        </p>
      )}

      {ecran === 'encaisser' && <Encaisser devises={dev} contrat={cible}
        onFini={(m) => { fermerEncaissement(); setMsg(m); recharger(); rechargerAlertes(); }}
        onFermer={fermerEncaissement} />}
      {ecran === 'change' && <Change devises={dev} onFini={(m) => { setEcran(''); setMsg(m); recharger(); }} onFermer={() => setEcran('')} />}
      {ecran === 'cloture' && <Cloture tiroirs={t} onFini={(m) => { setEcran(''); setMsg(m); recharger(); }} onFermer={() => setEcran('')} />}
      {ecran === 'taux' && <Taux devises={dev} onFini={(m) => { setEcran(''); setMsg(m); recharger(); }} onFermer={() => setEcran('')} />}
    </>
  );
}

/** Paiement mixte : un meme reglement en plusieurs devises. */
/** Une clé technique ne se montre pas : l'agent lit « Chèque », pas « cheque ». */
const LIBELLE_MODE = (): Record<string, string> => T.caisse.modes;

function Encaisser({ devises, contrat, onFini, onFermer }:
  { devises: Devises; contrat: Contrat | null; onFini: (m: string) => void; onFermer: () => void }) {
  const tx = T.caisse;
  const [libelle, setLibelle] = useState(contrat
    ? tx.encaissement.libelleContrat(contrat.numero, nomClient(contrat.customer as never))
    : tx.encaissement.libelleDefaut);
  const [lignes, setLignes] = useState([{
    devise: devises.deviseReference,
    montant: contrat && contrat.soldeMinor > 0 ? (contrat.soldeMinor / 100).toFixed(2) : '',
    mode: 'especes',
  }]);
  const [erreur, setErreur] = useState('');

  const tauxDe = (d: string) => d === devises.deviseReference ? 100 : (devises.taux.find((t) => t.devise === d)?.tauxAppliqueMinor ?? 0);
  const total = lignes.reduce((a, l) => a + Math.round((centimes(Number(l.montant || 0)) * tauxDe(l.devise)) / 100), 0);
  /*
   * Sans taux du jour, l'encaissement est refuse par le serveur — et le bouton
   * restait simplement gris, sans un mot. L'agent voyait un bouton mort et
   * n'avait aucun moyen de deviner qu'il manque un taux, ni ou le saisir.
   */
  const sansTaux = lignes
    .filter((l) => Number(l.montant) > 0 && l.devise !== devises.deviseReference && tauxDe(l.devise) === 0)
    .map((l) => l.devise);

  /*
   * UN PAIEMENT NE PART QU'UNE FOIS.
   *
   * Le 19 octobre, 18 000 DZD ont été encaissés deux fois à une minute d'écart :
   * le bouton restait actif pendant l'envoi, et le paiement a été ressaisi. Le
   * bouton se bloque désormais pendant l'envoi, et si le serveur reconnaît une
   * ressaisie, il faut confirmer explicitement que c'est un second versement.
   */
  const [envoi, setEnvoi] = useState(false);
  const [doublon, setDoublon] = useState(false);
  async function envoyer(confirmerDoublon = false) {
    if (envoi) return;
    setEnvoi(true);
    setErreur('');
    try {
      await api.post('/caisse/paiement-mixte', {
        confirmerDoublon,
        libelle,
        // Sans `contract`, le serveur encaisse mais ne solde rien.
        contract: contrat?._id,
        reservation: contrat ? (typeof contrat.reservation === 'string' ? contrat.reservation : contrat.reservation._id) : undefined,
        customer: contrat ? (typeof contrat.customer === 'string' ? contrat.customer : contrat.customer._id) : undefined,
        lignes: lignes.filter((l) => Number(l.montant) > 0)
          .map((l) => ({ devise: l.devise, montantMinor: centimes(Number(l.montant)), mode: l.mode })),
      });
      onFini(contrat ? tx.encaissement.rattache(contrat.numero) : tx.encaissement.enregistre);
    } catch (e) {
      setDoublon(e instanceof ApiError && e.code === 'DOUBLON_PROBABLE');
      setErreur(e instanceof ApiError ? e.erreur : tx.encaissement.erreur);
    } finally { setEnvoi(false); }
  }

  return (
    <Modale titre={contrat ? tx.encaissement.titreContrat(contrat.numero) : tx.encaissement.titre} onFermer={onFermer}>
      {contrat
        ? <div style={{ marginBottom: 12 }}><Bandeau type="info" titre={tx.encaissement.resteDu(montant(contrat.soldeMinor))}>
            {tx.encaissement.resteDuTexte}
          </Bandeau></div>
        : <div style={{ marginBottom: 12 }}><Bandeau type="warn" titre={tx.encaissement.nonRattache}>
            {tx.encaissement.nonRattacheTexte}
          </Bandeau></div>}
      <Champ label={tx.encaissement.libelle}><input value={libelle} onChange={(e) => setLibelle(e.target.value)} /></Champ>
      {sansTaux.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Bandeau type="ko" titre={tx.encaissement.sansTaux([...new Set(sansTaux)].join(', '))}>
            {tx.encaissement.sansTauxAvant(devises.deviseReference)}<b>{tx.encaissement.sansTauxGras}</b>{tx.encaissement.sansTauxApres}
          </Bandeau>
        </div>
      )}
      <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
        {lignes.map((l, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 110px auto', gap: 8, alignItems: 'end' }}>
            <Champ label={i === 0 ? tx.encaissement.montant : ''}>
              <input type="number" inputMode="decimal" value={l.montant}
                     onChange={(e) => setLignes(lignes.map((x, j) => j === i ? { ...x, montant: e.target.value } : x))} />
            </Champ>
            <Champ label={i === 0 ? tx.encaissement.devise : ''}>
              <select value={l.devise} onChange={(e) => setLignes(lignes.map((x, j) => j === i ? { ...x, devise: e.target.value } : x))}>
                {devises.devises.map((d) => <option key={d.code} value={d.code}>{d.code}</option>)}
              </select>
            </Champ>
            <Champ label={i === 0 ? tx.encaissement.payeEn : ''}>
              <select value={l.mode} onChange={(e) => setLignes(lignes.map((x, j) => j === i ? { ...x, mode: e.target.value } : x))}>
                {Object.entries(LIBELLE_MODE()).map(([k, lib]) => <option key={k} value={k}>{lib}</option>)}
              </select>
            </Champ>
            {lignes.length > 1 && <button className="btn btn-s btn-f" onClick={() => setLignes(lignes.filter((_, j) => j !== i))}>{tx.encaissement.retirer}</button>}
          </div>
        ))}
        {lignes.map((l, i) => l.devise !== devises.deviseReference && Number(l.montant) > 0 ? (
          <div key={`c${i}`} className="mini sourdine">
            {tx.encaissement.conversion(Number(l.montant).toFixed(2), l.devise, montant(Math.round((centimes(Number(l.montant)) * tauxDe(l.devise)) / 100)), (tauxDe(l.devise) / 100).toFixed(2))}
          </div>
        ) : null)}
        <button className="btn btn-s" onClick={() => setLignes([...lignes, { devise: 'EUR', montant: '', mode: 'especes' }])}>
          {tx.encaissement.ajouterDevise}
        </button>

        {/*
          * On annonce OÙ l'argent tombe, avant de valider.
          * La caisse se clôture par un comptage physique : un chèque rangé dans
          * le tiroir « espèces » rendrait l'écart faux tous les soirs.
          */}
        {lignes.some((l) => Number(l.montant) > 0 && l.mode !== 'especes') && (
          <Bandeau type="info" titre={tx.encaissement.banqueTitre}>
            {tx.encaissement.banqueTexte(lignes.filter((l) => Number(l.montant) > 0 && l.mode !== 'especes')
              .map((l) => `${Number(l.montant).toFixed(2)} ${l.devise} (${LIBELLE_MODE()[l.mode] ?? l.mode})`)
              .join(', '))}
          </Bandeau>
        )}
      </div>
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--bord)' }}>
        <b>{tx.encaissement.totalEn(devises.deviseReference)}</b><b className="montant" style={{ fontSize: 20 }}>{montant(total)}</b>
      </div>
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type={doublon ? 'warn' : 'ko'}>{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>{T.commun.actions.annuler}</button>
        {doublon && (
          <button className="btn" disabled={envoi} onClick={() => void envoyer(true)}>{tx.encaissement.confirmerDoublon}</button>
        )}
        <button className="btn btn-p" disabled={envoi || doublon || total <= 0 || sansTaux.length > 0}
                onClick={() => void envoyer()}>{tx.encaissement.titre}</button>
      </div>
    </Modale>
  );
}

function Change({ devises, onFini, onFermer }: { devises: Devises; onFini: (m: string) => void; onFermer: () => void }) {
  const tx = T.caisse;
  const [ds, setDs] = useState('EUR'); const [ms, setMs] = useState('');
  const [de, setDe] = useState(devises.deviseReference); const [me, setMe] = useState('');
  const [erreur, setErreur] = useState('');
  const taux = (d: string) => d === devises.deviseReference ? 100 : (devises.taux.find((t) => t.devise === d)?.tauxAppliqueMinor ?? 0);
  const vSortie = Math.round((centimes(Number(ms || 0)) * taux(ds)) / 100);
  const vEntree = Math.round((centimes(Number(me || 0)) * taux(de)) / 100);

  async function envoyer() {
    setErreur('');
    try {
      await api.post('/caisse/change', {
        deviseSortie: ds, montantSortieMinor: centimes(Number(ms)),
        deviseEntree: de, montantEntreeMinor: centimes(Number(me)),
      });
      onFini(tx.change.enregistre);
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : tx.encaissement.erreur); }
  }

  return (
    <Modale titre={tx.operationChange} onFermer={onFermer}>
      <div className="grille g2">
        <div>
          <div className="micro">{tx.change.sortie}</div>
          <Champ label={tx.encaissement.montant}><input type="number" value={ms} onChange={(e) => setMs(e.target.value)} /></Champ>
          <Champ label={tx.encaissement.devise}><select value={ds} onChange={(e) => setDs(e.target.value)}>{devises.devises.map((d) => <option key={d.code}>{d.code}</option>)}</select></Champ>
        </div>
        <div>
          <div className="micro">{tx.change.entree}</div>
          <Champ label={tx.encaissement.montant}><input type="number" value={me} onChange={(e) => setMe(e.target.value)} /></Champ>
          <Champ label={tx.encaissement.devise}><select value={de} onChange={(e) => setDe(e.target.value)}>{devises.devises.map((d) => <option key={d.code}>{d.code}</option>)}</select></Champ>
        </div>
      </div>
      {vSortie > 0 && vEntree > 0 && (
        <div style={{ marginTop: 12 }}>
          <Bandeau type={vEntree - vSortie >= 0 ? 'ok' : 'warn'}
                   titre={tx.change.resultat(vEntree - vSortie >= 0, montant(Math.abs(vEntree - vSortie)))}>
            {tx.change.detail(montant(vSortie), montant(vEntree))}
          </Bandeau>
        </div>
      )}
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>{T.commun.actions.annuler}</button>
        <button className="btn btn-p" disabled={!vSortie || !vEntree} onClick={() => void envoyer()}>{tx.change.bouton}</button>
      </div>
    </Modale>
  );
}

function Cloture({ tiroirs, onFini, onFermer }: { tiroirs: Tiroirs; onFini: (m: string) => void; onFermer: () => void }) {
  const tx = T.caisse;
  const [c, setC] = useState<Record<string, { compte: string; motif: string }>>(
    Object.fromEntries(tiroirs.tiroirs.map((t) => [t.devise, { compte: String(t.soldeMinor / 100), motif: '' }])));
  const [erreur, setErreur] = useState('');

  async function envoyer() {
    setErreur('');
    try {
      await api.post('/caisse/cloture', {
        comptages: tiroirs.tiroirs.map((t) => ({
          devise: t.devise, compteMinor: centimes(Number(c[t.devise]?.compte ?? 0)), motif: c[t.devise]?.motif,
        })),
      });
      onFini(tx.cloture.faite);
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : tx.encaissement.erreur); }
  }

  return (
    <Modale titre={tx.cloture.titre} onFermer={onFermer}>
      <div style={{ display: 'grid', gap: 12 }}>
        {tiroirs.tiroirs.map((t) => {
          const compte = centimes(Number(c[t.devise]?.compte ?? 0));
          const ecart = compte - t.soldeMinor;
          return (
            <div key={t.devise} style={{ borderBottom: '1px solid var(--bord)', paddingBottom: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr 1fr', gap: 10, alignItems: 'end' }}>
                <b>{t.devise}</b>
                <Champ label={tx.cloture.theorique}><input readOnly value={montant(t.soldeMinor, '', t.decimales)} /></Champ>
                <Champ label={tx.cloture.compte}>
                  <input type="number" value={c[t.devise]?.compte ?? ''}
                         onChange={(e) => setC({ ...c, [t.devise]: { compte: e.target.value, motif: c[t.devise]?.motif ?? '' } })} />
                </Champ>
              </div>
              {ecart !== 0 && (
                <div style={{ marginTop: 8 }}>
                  <Champ label={tx.cloture.ecart(montant(ecart, t.devise, t.decimales))}>
                    <input value={c[t.devise]?.motif ?? ''}
                           onChange={(e) => setC({ ...c, [t.devise]: { compte: c[t.devise]?.compte ?? '', motif: e.target.value } })} />
                  </Champ>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>{T.commun.actions.annuler}</button>
        <button className="btn btn-p" onClick={() => void envoyer()}>{tx.cloture.bouton}</button>
      </div>
    </Modale>
  );
}

function Taux({ devises, onFini, onFermer }: { devises: Devises; onFini: (m: string) => void; onFermer: () => void }) {
  const tx = T.caisse;
  const etrangeres = devises.devises.filter((d) => d.code !== devises.deviseReference);
  const [v, setV] = useState<Record<string, string>>(Object.fromEntries(
    etrangeres.map((d) => [d.code, String((devises.taux.find((t) => t.devise === d.code)?.tauxAppliqueMinor ?? 0) / 100)])));
  const [erreur, setErreur] = useState('');

  async function envoyer() {
    setErreur('');
    try {
      for (const d of etrangeres) {
        const val = centimes(Number(v[d.code] ?? 0));
        if (val > 0) await api.post('/caisse/taux', { devise: d.code, tauxReferenceMinor: val, tauxAppliqueMinor: val });
      }
      onFini(tx.taux.misAJour);
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : tx.encaissement.erreur); }
  }

  return (
    <Modale titre={tx.taux.titre} onFermer={onFermer}>
      <p className="mini sourdine" style={{ marginBottom: 12 }}>
        {tx.taux.texteAvant}<b>{tx.taux.texteGras}</b>{tx.taux.texteApres}
      </p>
      <div style={{ display: 'grid', gap: 10 }}>
        {etrangeres.map((d) => (
          <Champ key={d.code} label={`1 ${d.code} = ? ${devises.deviseReference}`}>
            <input type="number" step="0.01" value={v[d.code] ?? ''} onChange={(e) => setV({ ...v, [d.code]: e.target.value })} />
          </Champ>
        ))}
      </div>
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>{T.commun.actions.annuler}</button>
        <button className="btn btn-p" onClick={() => void envoyer()}>{tx.taux.bouton}</button>
      </div>
    </Modale>
  );
}
